import { DxCodeEntryInput } from "../../../../../components/modals/editDxCodeSidePanel/editDxCodeSidePanel";
import { DrugData } from "../../../../../data-model/drugData";
import {
  NccnIcdCodeEntry,
  NccnIcdConfig,
} from "../../../../../data-model/nccnIcdConfig";
import { NccnRegimenConfig } from "../../../../../data-model/nccnRegimenConfig";

/**
 * Formats NCCN ICD code entries for table display: extracts each entry's `code`,
 * drops empty values, and joins with `", "`. Returns an empty string when `entries`
 * is undefined or empty.
 *
 * @param entries - ICD code entries from config, or undefined
 * @returns Comma-separated ICD code strings for UI columns (e.g. drug/regimen dx codes)
 */
export const formatIcdCodeEntriesForDisplay = (
  entries: NccnIcdCodeEntry[] | undefined,
): string =>
  entries
    ?.map((e) => e.code)
    .filter(Boolean)
    .join(", ") ?? "";

/**
 * Transforms NCCN ICD config data into drug table data format
 */
export const transformDrugTableData = (
  nccnIcdConfigData: NccnIcdConfig[],
): (DrugData & { actions: DrugData })[] => {
  return nccnIcdConfigData
    .map((config) => {
      const dxCodesString = formatIcdCodeEntriesForDisplay(config.codes);
      const jCode = config.code || config.id || "";

      return {
        id: config.id || "",
        jCode: jCode,
        drugName: config.category || "",
        dxCodes: dxCodesString,
        actions: {
          id: config.id || "",
          jCode: jCode,
          drugName: config.category || "",
          dxCodes: dxCodesString,
        },
      };
    })
    .filter((drug) => drug.id);
};

/**
 * Transforms NCCN Regimen config data into regimen table data format
 */
export const transformRegimenTableData = (
  nccnRegimenConfigData: NccnRegimenConfig[],
) => {
  return nccnRegimenConfigData
    .map((config) => {
      const dxCodesString = formatIcdCodeEntriesForDisplay(config.icdcodes);

      return {
        id: config.id || "",
        regimenCode: config.regimen_code || "",
        regimenName: config.regimen_name || "",
        dxCodes: dxCodesString,
        actions: {
          id: config.id || "",
          regimenCode: config.regimen_code || "",
          regimenName: config.regimen_name || "",
          dxCodes: dxCodesString,
        },
      };
    })
    .filter((regimen) => regimen.id);
};

/**
 * Creates a priority config object from DxCodeEntryInput array
 */
export const createPriorityConfig = (
  dxCodes: DxCodeEntryInput[],
): { [icdCode: string]: number } => {
  const priorityConfig: { [icdCode: string]: number } = {};
  dxCodes.forEach((entry) => {
    // Only include priority if it's defined
    if (entry.priority !== undefined) {
      priorityConfig[entry.dxCode] = entry.priority;
    }
  });
  return priorityConfig;
};

/**
 * Builds Firestore-shaped `{ code, source }[]` from edited rows.
 *
 * Used only for the global regimen config's `icdcodes`. Regimens have no per-code
 * rank — ordering there still comes from `priority_config_org_specific`.
 */
export const buildIcdCodesForPersist = (
  dxCodes: DxCodeEntryInput[],
): NccnIcdCodeEntry[] =>
  dxCodes.map((entry) => ({
    code: entry.dxCode,
    source: entry.source ?? [],
  }));

/**
 * Builds the per-org drug config's `codes` array from edited rows, carrying the
 * per-code `rank` the engine reads from `healthcare_facility/{org}/dx_code_config`.
 *
 * The UI Priority value is persisted verbatim as `rank` (a seeded rank-0 pin
 * round-trips as `0`); an unranked row omits `rank` so the engine falls through
 * to recency.
 *
 * @param dxCodes - Edited rows (`priority` mirrors the Priority input)
 * @returns `[{ code, source, rank? }]` in the engine's shape
 */
export const buildDrugCodesForPersist = (
  dxCodes: DxCodeEntryInput[],
): NccnIcdCodeEntry[] =>
  dxCodes.map((entry) => {
    const item: NccnIcdCodeEntry = {
      code: entry.dxCode,
      source: entry.source ?? [],
    };
    if (entry.priority !== undefined) {
      item.rank = entry.priority;
    }
    return item;
  });
